<script setup>
import { inject, computed } from "vue";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/v4/dialog";
import {
  IconPhoneOff,
  IconPhone,
  IconClock,
  IconChecks,
  IconX,
} from "@tabler/icons-vue";

const jssip = inject("jssip", null);

// Only show for global calls (from dialpad), not for lead/follow-up calls
const lastCall = computed(() => jssip?.lastCallEnded?.value ?? null);
const isOpen = computed(
  () => !!lastCall.value && lastCall.value.wasGlobalCall === true
);

const wasConnected = computed(() => lastCall.value?.wasConnected ?? false);

const displayNumber = computed(() => lastCall.value?.number ?? "");

const duration = computed(() => lastCall.value?.duration ?? "");

const handleClose = () => {
  jssip?.clearLastCallEnded?.();
};
</script>

<template>
  <Dialog :open="isOpen" @update:open="(v) => !v && handleClose()">
    <DialogContent
      class="!vf-max-w-md !vf-rounded-xl !vf-p-0 !vf-overflow-hidden"
      @escapeKeyDown="handleClose"
      @pointerDownOutside="handleClose"
    >
      <!-- Header with border separator -->
      <DialogHeader
        class="vf-flex vf-flex-row vf-items-center vf-gap-2 vf-p-6 vf-pb-4 vf-text-left vf-border-b vf-border-neutral-200"
      >
        <DialogTitle
          class="vf-text-xl vf-text-neutral-900 vf-flex vf-items-center vf-justify-start vf-gap-2"
        >
          <IconPhoneOff
            size="24"
            class="vf-text-neutral-600 vf-shrink-0"
            stroke-width="1.5"
          />
          Call Ended
        </DialogTitle>
      </DialogHeader>

      <div class="vf-flex vf-flex-col vf-gap-6 vf-p-6 vf-pt-5">
        <!-- Status section -->
        <div class="vf-flex vf-flex-col vf-items-center vf-gap-3">
          <div
            class="vf-flex vf-h-[72px] vf-w-[72px] vf-items-center vf-justify-center vf-rounded-xl vf-shrink-0"
            :class="wasConnected ? 'vf-bg-success-100' : 'vf-bg-danger-100'"
          >
            <IconChecks
              v-if="wasConnected"
              size="36"
              class="vf-text-success-600"
              stroke-width="2"
            />
            <IconX
              v-else
              size="36"
              class="vf-text-danger-600"
              stroke-width="2"
            />
          </div>
          <span
            class="vf-text-lg"
            :class="wasConnected ? 'vf-text-success-600' : 'vf-text-danger-600'"
          >
            {{ wasConnected ? "Connected" : "Not Connected" }}
          </span>
          <p
            class="vf-text-center vf-text-sm vf-text-neutral-500 vf-leading-relaxed"
          >
            {{
              wasConnected
                ? "Call was successfully connected and completed"
                : "Call was Not successfully connected"
            }}
          </p>
        </div>

        <!-- Call details -->
        <div
          class="vf-flex vf-items-stretch vf-gap-6 vf-rounded-xl vf-bg-neutral-100 vf-p-4"
        >
          <div class="vf-flex vf-flex-1 vf-min-w-0 vf-flex-col vf-gap-1.5">
            <div class="vf-flex vf-items-center vf-gap-1.5 vf-text-neutral-500">
              <IconPhone size="16" class="vf-shrink-0" stroke-width="1.5" />
              <span class="vf-text-xs vf-font-medium">Phone Number</span>
            </div>
            <span class="vf-text-base vf-text-neutral-900 vf-tracking-tight">
              {{ displayNumber || "—" }}
            </span>
          </div>
          <div
            v-if="wasConnected && duration"
            class="vf-flex vf-flex-1 vf-min-w-0 vf-flex-col vf-gap-1.5 vf-border-l vf-border-neutral-200 vf-pl-6"
          >
            <div class="vf-flex vf-items-center vf-gap-1.5 vf-text-neutral-500">
              <IconClock size="16" class="vf-shrink-0" stroke-width="1.5" />
              <span class="vf-text-xs vf-font-medium">Call Duration</span>
            </div>
            <span class="vf-text-base vf-text-neutral-900 vf-tracking-tight">
              {{ duration }}
            </span>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
